import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RefundDetailsModal = ({ refund, onClose }) => {
  if (!refund) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white font-heading">Refund Request Details</h2>
            <p className="text-sm text-slate-400 mt-1 font-body">{refund?.requestId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <Icon name="X" size={20} color="#94A3B8" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Customer Information */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3 font-body">
              Customer Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-400 font-caption">Customer Name</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{refund?.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Email</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{refund?.customerEmail}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Account Age</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{refund?.customerHistory?.accountAge}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Total Purchases</p>
                <p className="text-sm text-white font-medium mt-1 font-body">₹{refund?.customerHistory?.totalPurchases?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Previous Refunds</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{refund?.customerHistory?.refundHistory}</p>
              </div>
            </div>
          </div>

          {/* Transaction Details */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3 font-body">
              Transaction Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-400 font-caption">Transaction ID</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{refund?.transactionId}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Original Amount</p>
                <p className="text-sm text-white font-medium mt-1 font-body">₹{refund?.originalAmount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Refund Amount</p>
                <p className="text-sm text-amber-400 font-bold mt-1 font-body">₹{refund?.refundAmount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Request Date</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{new Date(refund.requestDate)?.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Refund Reason */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3 font-body">
              Refund Reason
            </h3>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-slate-400 font-caption">Reason Code</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{refund?.reasonCode}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Detailed Reason</p>
                <p className="text-sm text-slate-300 mt-1 font-body">{refund?.reason}</p>
              </div>
            </div>
          </div>

          {/* Priority & Status */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3 font-body">
              Request Status
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-400 font-caption">Priority</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border mt-1 ${
                  refund?.priority === 'High' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                  refund?.priority === 'Medium'? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                }`}>
                  {refund?.priority}
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border mt-1 ${
                  refund?.status === 'Pending' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                  refund?.status === 'Approved'? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'
                }`}>
                  {refund?.status}
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Assigned To</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{refund?.assignedTo}</p>
              </div>
            </div>
          </div>

          {/* Approval/Rejection Details */}
          {(refund?.status === 'Approved' || refund?.status === 'Rejected') && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3 font-body">
                {refund?.status} Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400 font-caption">{refund?.status} By</p>
                  <p className="text-sm text-white font-medium mt-1 font-body">{refund?.approvedBy || refund?.rejectedBy}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 font-caption">{refund?.status} Date</p>
                  <p className="text-sm text-white font-medium mt-1 font-body">
                    {new Date(refund.approvedDate || refund.rejectedDate)?.toLocaleString()}
                  </p>
                </div>
                {refund?.rejectionReason && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-400 font-caption">Rejection Reason</p>
                    <p className="text-sm text-slate-300 mt-1 font-body">{refund?.rejectionReason}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Attachments */}
          {refund?.attachments?.length > 0 && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3 font-body">
                Attachments
              </h3>
              <div className="space-y-2">
                {refund?.attachments?.map((attachment, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-900 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Icon name="Paperclip" size={16} color="#94A3B8" />
                      <span className="text-sm text-white font-body">{attachment}</span>
                    </div>
                    <button className="text-amber-400 hover:text-amber-300 text-sm font-medium">
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dispute Information */}
          {refund?.disputeInfo && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} color="#F87171" />
                <div>
                  <h3 className="text-sm font-semibold text-red-400 font-body">Dispute Filed</h3>
                  <p className="text-sm text-slate-300 mt-1 font-body">
                    Dispute ID: {refund?.disputeInfo?.disputeId} | Filed: {new Date(refund.disputeInfo.filedDate)?.toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 px-6 py-4 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RefundDetailsModal;